/**
 * Live-søgning i headeren: debounced multi-ords-søgning i det cachede
 * katalog (respekterer produktadgang).
 *
 * To varianter:
 *  - "desktop" (default): inline felt med resultat-dropdown under.
 *  - "mobile": feltet er en trigger, der åbner en FULDSKÆRMS-søgeoplevelse
 *    (app-standard) — autofokus, store trykflader, Annuller, resultater
 *    der fylder hele skærmen.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useProducts } from "@/features/catalog/hooks/useCatalog";
import { usePriceVisibility } from "@/features/catalog/PriceVisibilityContext";
import { useAllowedProductIds, filterAllowedProducts } from "@/features/catalog/hooks/useAllowedProducts";
import { useEffectivePrices } from "@/features/catalog/hooks/useEffectivePrice";
import { productMatchesSearch, type ShopifyProduct } from "@/features/catalog/types";
import { formatPrice } from "@/features/catalog/lib/format";

const MAX_RESULTS = 8;
const MAX_RESULTS_FULLSCREEN = 25;

/** Fælles søgetilstand (debounce + resultater) for begge varianter. */
function useLiveSearch(maxResults: number) {
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");

  const { data: products } = useProducts();
  const allowedIds = useAllowedProductIds();
  const pricing = useEffectivePrices();

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(timer);
  }, [term]);

  const results = useMemo(() => {
    if (!debounced || !products) return [];
    return filterAllowedProducts(products, allowedIds)
      .filter((p) => productMatchesSearch(p, debounced))
      .slice(0, maxResults);
  }, [debounced, products, allowedIds, maxResults]);

  return { term, setTerm, debounced, results, pricing };
}

/** Én resultat-række (deles af dropdown og fuldskærm). */
function ResultRow({
  product,
  pricing,
  onPick,
  large = false,
}: {
  product: ShopifyProduct;
  pricing: ReturnType<typeof useEffectivePrices>;
  onPick: () => void;
  large?: boolean;
}) {
  const { hidePrices } = usePriceVisibility();
  const variant = product.variants[0];
  const price = variant && !hidePrices ? pricing.resolve(variant, product.id) : null;
  return (
    <Link
      to={`/produkter/${product.handle}`}
      onClick={onPick}
      className={
        large
          ? "flex min-h-24 items-center gap-4 px-4 py-3 active:bg-accent"
          : "flex items-center gap-3 px-3 py-2 hover:bg-accent"
      }
    >
      {product.images[0]?.src ? (
        <img
          src={product.images[0].src}
          alt=""
          loading="lazy"
          className={
            large
              ? "h-20 w-20 shrink-0 rounded-xl border border-border/60 bg-white object-contain p-1.5"
              : "h-10 w-10 rounded object-cover"
          }
        />
      ) : (
        <div
          className={large ? "h-20 w-20 shrink-0 rounded-xl bg-muted" : "h-10 w-10 rounded bg-muted"}
          aria-hidden
        />
      )}
      <div className="min-w-0 flex-1">
        <p
          className={
            large
              ? "line-clamp-2 text-base font-semibold leading-snug text-foreground"
              : "truncate text-sm font-medium"
          }
        >
          {product.title}
        </p>
        {variant?.sku && (
          <p
            className={
              large
                ? "mt-0.5 truncate text-[13px] text-muted-foreground"
                : "truncate text-xs text-muted-foreground"
            }
          >
            {variant.sku}
          </p>
        )}
      </div>
      {price && (
        <span
          className={
            large
              ? "whitespace-nowrap font-mono text-base font-bold tabular-nums text-foreground"
              : "whitespace-nowrap font-mono text-sm font-semibold tabular-nums"
          }
        >
          {formatPrice(price.priceExVat)}
        </span>
      )}
    </Link>
  );
}

/** Fuldskærms-søgning (mobil): eget overlay med autofokus og Annuller. */
function FullscreenSearch({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { term, setTerm, debounced, results, pricing } = useLiveSearch(MAX_RESULTS_FULLSCREEN);
  const inputRef = useRef<HTMLInputElement>(null);

  // Autofokus (åbner tastaturet) + lås baggrundens scroll mens overlayet er
  // åbent. preventScroll + scrollTo(0,0) forhindrer iOS/WKWebView i at skubbe
  // det fixed-positionerede overlay ned, når tastaturet åbner.
  useEffect(() => {
    window.scrollTo(0, 0);
    inputRef.current?.focus({ preventScroll: true });
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const submitSearch = () => {
    if (!debounced) return;
    onClose();
    navigate(`/produkter?soeg=${encodeURIComponent(debounced)}`);
  };

  // Portal til <body>: ingen forfader med transform/filter kan forskyde
  // overlayet, og h-[100dvh] følger tastaturets viewport-ændring korrekt.
  return createPortal(
    <div className="fixed inset-x-0 top-0 z-[60] flex h-[100dvh] flex-col bg-background">
      {/* Topbar: felt + Annuller */}
      <div className="flex items-center gap-2 border-b border-border bg-background px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.625rem)]">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3"
            aria-hidden
          />
          <Input
            ref={inputRef}
            type="search"
            aria-label="Søg i produkter"
            placeholder="Søg varer eller varenr."
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && debounced) submitSearch();
              if (e.key === "Escape") onClose();
            }}
            className="min-h-11 rounded-xl border border-input bg-white pl-10 pr-10 text-base text-ink shadow-sm placeholder:text-ink-3"
          />
          {term && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Ryd søgning"
              className="absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2 text-ink-3"
              onClick={() => {
                setTerm("");
                inputRef.current?.focus();
              }}
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-md px-1.5 py-2 text-sm font-semibold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Annuller
        </button>
      </div>

      {/* Resultater fylder resten af skærmen */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
        {debounced.length === 0 ? (
          <div className="px-6 pt-14 text-center">
            <Search className="mx-auto h-8 w-8 text-ink-3" aria-hidden />
            <p className="mt-3 text-sm font-medium text-foreground">Søg i hele sortimentet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Skriv et varenavn, mærke eller varenummer.
            </p>
          </div>
        ) : results.length === 0 ? (
          <div className="px-6 pt-14 text-center">
            <p className="text-sm font-medium text-foreground">
              Ingen produkter matcher &quot;{debounced}&quot;
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Prøv et andet ord, eller tjek stavningen.
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-border/60">
              {results.map((product) => (
                <li key={product.id}>
                  <ResultRow
                    product={product}
                    pricing={pricing}
                    onPick={onClose}
                    large
                  />
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={submitSearch}
              className="flex w-full items-center justify-center gap-1.5 px-4 py-4 text-sm font-semibold text-brand"
            >
              Se alle resultater for &quot;{debounced}&quot;
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

interface LiveSearchProps {
  /** "mobile": felt som trigger + fuldskærms-overlay. Default: inline dropdown. */
  variant?: "desktop" | "mobile";
}

export function LiveSearch({ variant = "desktop" }: LiveSearchProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { term, setTerm, debounced, results, pricing } = useLiveSearch(MAX_RESULTS);

  useEffect(() => {
    if (variant !== "desktop") return;
    const onClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [variant]);

  // ----- Mobil: trigger-felt + fuldskærms-overlay ---------------------------
  if (variant === "mobile") {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Åbn søgning"
          className="flex min-h-11 w-full items-center gap-2.5 rounded-xl border border-input bg-white px-3.5 text-base text-ink-3 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Search className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">Søg varer eller varenr.</span>
        </button>
        {open && <FullscreenSearch onClose={() => setOpen(false)} />}
      </>
    );
  }

  // ----- Desktop: inline felt med dropdown ----------------------------------
  const showDropdown = open && debounced.length > 0;

  const submitSearch = () => {
    if (!debounced) return;
    setOpen(false);
    navigate(`/produkter?soeg=${encodeURIComponent(debounced)}`);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Input
          type="search"
          role="combobox"
          aria-expanded={showDropdown}
          aria-label="Søg i produkter"
          placeholder="Søg varer eller varenr."
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && debounced) submitSearch();
            if (e.key === "Escape") setOpen(false);
          }}
          className="min-h-11 rounded-xl border border-input bg-white pl-4 pr-[5.5rem] text-base text-ink shadow-sm placeholder:text-ink-3"
        />
        {term && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Ryd søgning"
            className="absolute right-12 top-1/2 h-8 w-8 -translate-y-1/2 text-ink-3 hover:bg-muted"
            onClick={() => {
              setTerm("");
              setOpen(false);
            }}
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        )}
        <button
          type="button"
          aria-label="Søg"
          onClick={submitSearch}
          className="absolute right-1 top-1/2 flex h-9 w-10 -translate-y-1/2 items-center justify-center rounded-md bg-cta text-white transition-colors hover:bg-cta-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <Search className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-lg">
          {results.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              Ingen produkter matcher &quot;{debounced}&quot;.
            </p>
          ) : (
            <ul className="max-h-96 overflow-y-auto py-1">
              {results.map((product) => (
                <li key={product.id}>
                  <ResultRow
                    product={product}
                    pricing={pricing}
                    onPick={() => {
                      setOpen(false);
                      setTerm("");
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
